import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  SparklesIcon,
  CurrencyYenIcon,
  ChatBubbleLeftRightIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import BookingCalendar from './BookingCalendar'
import toast from 'react-hot-toast'
import { DateHelper } from '../utils/dateHelper'

interface Course {
  id: string
  title: string
  description: string
  subject: string
  finalPricePerHour: number
  duration: number
  teacher: {
    id: string
    user: {
      firstName: string
      lastName: string
    }
    title: string
    rating: string | number
  }
}

interface Schedule {
  id: string
  date: string
  startTime: string
  endTime: string
  status: 'available' | 'booked' | 'blocked'
}

const BookingForm: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()

  const [course, setCourse] = useState<Course | null>(null)
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null)
  const [selectedDuration, setSelectedDuration] = useState(1)
  const [customStartTime, setCustomStartTime] = useState('')
  const [customEndTime, setCustomEndTime] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(1) // 1: Select Duration/Time, 2: Confirm

  const durationOptions = [1, 1.5, 2, 2.5, 3]

  // 获取课程信息
  const fetchCourse = async () => {
    try {
      const response = await fetch(`/api/courses/${courseId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('获取课程信息失败')
      }

      const data = await response.json()
      // 处理不同的响应格式
      const courseData = data.data?.course || data.course || data
      console.log('预约页面获取到的课程数据:', courseData)
      
      // 确保数据结构正确
      if (courseData && courseData.teacher) {
        setCourse(courseData)
      } else if (courseData && courseData.instructor) {
        // 如果没有teacher但有instructor，构建兼容的数据结构
        setCourse({
          ...courseData,
          teacher: {
            id: courseData.instructor?.id || courseData.teacherId,
            user: {
              firstName: courseData.instructor?.firstName || '',
              lastName: courseData.instructor?.lastName || ''
            },
            title: '教师',
            rating: courseData.averageRating || '0'
          }
        })
      } else {
        setCourse(courseData)
      }
    } catch (err) {
      console.error('获取课程失败:', err)
      setError('获取课程信息失败')
    }
  }

  // 处理日历时间选择
  const handleTimeSlotSelect = (schedule: Schedule, startTime: string, endTime: string) => {
    setSelectedSchedule(schedule)
    setCustomStartTime(startTime)
    setCustomEndTime(endTime)
    setError('')
    toast.success('时间选择成功！')
    setStep(2)
  }

  useEffect(() => {
    if (courseId) {
      fetchCourse()
    }
  }, [courseId])

  useEffect(() => {
    if (course) {
      setLoading(false)
    }
  }, [course])

  // 计算结束时间
  useEffect(() => {
    if (customStartTime && selectedDuration) {
      const [hours, minutes] = customStartTime.split(':').map(Number)
      const totalMinutes = hours * 60 + minutes + selectedDuration * 60
      const endHours = Math.floor(totalMinutes / 60)
      const endMinutes = totalMinutes % 60
      setCustomEndTime(
        `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
      )
    }
  }, [customStartTime, selectedDuration])

  // 重置选择
  const handleResetSelection = () => {
    setSelectedSchedule(null)
    setCustomStartTime('')
    setCustomEndTime('')
    setStep(1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedSchedule || !customStartTime || !customEndTime) {
      setError('请选择时间安排')
      return
    }

    if (!course) {
      setError('课程信息缺失')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          courseId: course.id,
          scheduleId: selectedSchedule.id,
          date: selectedSchedule.date,
          startTime: customStartTime,
          endTime: customEndTime,
          duration: selectedDuration,
          notes
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '预约失败')
      }

      toast.success('预约成功！等待老师确认，正在跳转到仪表盘...')

      setTimeout(() => {
        navigate('/student/dashboard')
      }, 1500)
    } catch (err: any) {
      console.error('预约失败:', err)
      const errorMessage = err.message || '预约失败，请稍后重试'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 py-12">
        <div className="flex flex-col justify-center items-center h-64">
          <div className="relative mb-6">
            <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
            <SparklesIcon className="w-6 h-6 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-600 font-medium">加载课程信息...</p>
        </div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12">
            <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CalendarIcon className="w-10 h-10 text-rose-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-3">课程不存在</h3>
            <p className="text-slate-500 mb-6">该课程可能已被删除或暂不可用</p>
            <button
              onClick={() => navigate('/courses')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-indigo-200/50 transition-all"
            >
              返回课程列表
            </button>
          </div>
        </div>
      </div>
    )
  }

  const totalPrice = (Number(course.finalPricePerHour) || 0) * selectedDuration

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors group"
          >
            <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">返回</span>
          </button>

          {/* 步骤指示器 */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold transition-all ${
                  step >= 1
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {step > 1 ? <CheckIcon className="w-5 h-5" /> : '1'}
              </div>
              <span className={`text-sm font-medium ${step >= 1 ? 'text-slate-800' : 'text-slate-400'}`}>
                选择时间
              </span>
            </div>
            <div className="w-8 h-0.5 bg-slate-200 rounded" />
            <div className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold transition-all ${
                  step >= 2
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                2
              </div>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-slate-800' : 'text-slate-400'}`}>
                确认预约
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左侧：主要操作区 */}
          <div className="lg:col-span-2 space-y-6">
            {step === 1 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <ClockIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">选择课程时长与时间</h2>
                    <p className="text-sm text-slate-500">根据您的需求选择合适的学习时长</p>
                  </div>
                </div>

                {/* 时长选择 */}
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    您计划学习多长时间？
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {durationOptions.map((duration) => (
                      <button
                        key={duration}
                        onClick={() => setSelectedDuration(duration)}
                        className={`px-5 py-3 rounded-xl border-2 transition-all font-medium ${
                          selectedDuration === duration
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                            : 'border-slate-200 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50/50'
                        }`}
                      >
                        {duration} 小时
                        <span className="block text-xs mt-0.5 opacity-70">
                          ¥{((Number(course.finalPricePerHour) || 0) * duration).toFixed(0)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 日历组件 */}
                <div className="border-t border-slate-100 pt-6">
                  {course.teacher?.id ? (
                    <BookingCalendar
                      teacherId={course.teacher.id}
                      onTimeSlotSelect={handleTimeSlotSelect}
                      selectedDuration={selectedDuration}
                    />
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <p>暂无教师时间安排</p>
                      <p className="text-sm mt-2">请联系客服获取更多信息</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Step 2: 确认信息 */
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 animate-in slide-in-from-right duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">确认预约信息</h2>
                      <p className="text-sm text-slate-500">请核对以下信息后提交</p>
                    </div>
                  </div>
                  <button
                    onClick={handleResetSelection}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-semibold"
                  >
                    修改时间
                  </button>
                </div>

                {/* 选中的时间信息 */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6 border border-indigo-100">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      <CalendarIcon className="h-7 w-7 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-indigo-900 text-lg">
                        {selectedSchedule && DateHelper.formatChineseDate(selectedSchedule.date)}
                      </h3>
                      <p className="text-indigo-700 mt-1 text-xl font-semibold">
                        {customStartTime} - {customEndTime}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-sm text-indigo-600 bg-white px-3 py-1 rounded-lg">
                          <ClockIcon className="w-4 h-4" />
                          {selectedDuration} 小时
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                      <ChatBubbleLeftRightIcon className="w-4 h-4 text-slate-400" />
                      给老师的留言 (可选)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all resize-none"
                      placeholder="请告诉我您目前的学习进度、主要困难或本次课程的具体目标..."
                    />
                  </div>

                  {error && (
                    <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={handleResetSelection}
                      className="flex-1 px-6 py-3.5 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                    >
                      上一步
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3.5 rounded-xl font-bold hover:shadow-lg hover:shadow-indigo-200/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="inline-flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          提交中...
                        </span>
                      ) : (
                        '确认并支付'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* 右侧：课程摘要卡片 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden sticky top-24">
              {/* 顶部装饰 */}
              <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

              <div className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-indigo-500" />
                  课程摘要
                </h3>

                <div className="mb-6 pb-6 border-b border-slate-100">
                  <h4 className="font-bold text-slate-900 text-lg mb-3">{course.title}</h4>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                      {course.teacher?.user?.firstName?.[0] || '师'}
                      {course.teacher?.user?.lastName?.[0] || ''}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-800">
                        {course.teacher?.user?.firstName || ''} {course.teacher?.user?.lastName || '老师'}
                      </div>
                      <div className="text-xs text-slate-500">{course.teacher?.title || '教师'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-lg">
                      <StarIcon className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      {(Number(course.teacher?.rating) || 0).toFixed(1)}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg">
                      {course.subject || '综合'}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">单价</span>
                    <span className="font-semibold text-slate-800">¥{Number(course.finalPricePerHour) || 0}/小时</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">时长</span>
                    <span className="font-semibold text-slate-800">{selectedDuration} 小时</span>
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex justify-between items-center">
                    <span className="font-bold text-slate-900">总计</span>
                    <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      ¥{totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                {step === 1 && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl flex items-start gap-3 border border-amber-100">
                    <div className="text-xl">💡</div>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">温馨提示</p>
                      <p className="text-xs text-amber-700 mt-1">
                        请先选择您想要的上课日期和时间段，然后点击确认进入下一步。
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingForm
